import { i18n } from '@/commons/utils';
import { getStoredIdentifier, setStoredIdentifier, ajaxError, displayOptions, disableSubmit } from '../customer-utils';
import { disableForm } from './complete-account';
import { initSecondIdentifierOptionBlock } from './complete-account-options';

const services = {
  IDENTIFIER_EXISTS: 'rest/model/atg/userprofiling/ProfileActor/isIdentifierExists',
  SEND_VALIDATION_CODE: 'rest/model/atg/userprofiling/ProfileActor/send-mobile-verification-code',
};

let counters;
let mobileSecondIdentifier;

export const init = form => {
  counters = cube.$$('.js-resend-code-button');
  mobileSecondIdentifier = cube.$('#js-personal-data-mobile #mobile');
  initSecondIdentifierOptionBlock(form);

  counters.forEach(counter => {
    const $counter = $(counter);
    $counter.data('countdown', new window.decathlon.userAccount.Countdown($counter, 30));
  });

  if (mobileSecondIdentifier && cube.$id('js-personal-data-mobile').dataset.secondIdentifier) {
    mobileSecondIdentifier.on('input', () => validateMobileSecondIdentifier(mobileSecondIdentifier, form));

    const countryCodeSecondIdentifier = cube.$('#js-personal-data-mobile [name="mobileCountryCode"]');

    if (countryCodeSecondIdentifier)
      countryCodeSecondIdentifier.on('change', () =>
        validateMobileSecondIdentifier(mobileSecondIdentifier, form, true)
      );
  }

  const resendCodeButtons = cube.$$('.js-resend-code-button');
  if (resendCodeButtons.length) resendCodeButtons.on('click', resendValidationCode);

  const sendCodeButton = cube.$id('js-send-code-button');
  if (sendCodeButton) sendCodeButton.on('click', sendSecondIdentifierValidationCode);
};

/**
 * Dispatch action(s) if the customer's identifier already exists.
 *
 * @param {Array<String>} actions A list of actions.
 * @param {HTMLFormElement} form The create account form.
 * @param {String} identifierType 'MAIN' or 'SECOND' identifier.
 */
const dispatchActions = (actions, form) => {
  const ACTIONS = {
    'disable-form': () => disableForm(form),
    'disable-submit': () => disableSubmit(true, 'COMPLETE'),
  };

  actions.forEach(action => ACTIONS[action]());
};

/**
 * Validate the second identifier if it's a mobile.
 *
 * @param {HTMLFormElement} form The create account form.
 * @param {HTMLElement} secondIdentifier The second identifier.
 * @param {Boolean} validation true if the second identifier need a validation. (usefull when the country code is update).
 */
let timeout;
const validateMobileSecondIdentifier = (secondIdentifier, form, validation) => {
  const data = getStoredIdentifier('MAIN');
  const countryCodesecondIdentifier = cube.$('#js-personal-data-mobile [name="mobileCountryCode"]');
  const { regex } =
    countryCodesecondIdentifier.tagName === 'INPUT'
      ? countryCodesecondIdentifier.dataset
      : countryCodesecondIdentifier[countryCodesecondIdentifier.selectedIndex].dataset;

  if (validation) $(secondIdentifier).valid();

  displayOptions(false, 'SECOND');
  displayMobileValidation(false, null, 'SECOND');
  displaySecondIdentifierMobileValidationButton(false);
  disableSubmit(false, 'COMPLETE');

  if (
    secondIdentifier.value !== secondIdentifier.dataset.currentValue &&
    RegExp(regex).test(secondIdentifier.value)
  ) {
    const params = {
      login: secondIdentifier.value,
      countryCode: countryCodesecondIdentifier && countryCodesecondIdentifier.value,
      isCheckout: cube.$id('is-checkout').value,
      mainMemberType: data.memberType || 0,
    };

    $(secondIdentifier).valid();
    clearTimeout(timeout);

    timeout = setTimeout(() => {
      checkSecondIdentifierAvailaibility(secondIdentifier, form, params);
    }, 500);
  }
};

/**
 * Send an XHR to check if the second identifier is not already linked to another account.
 *
 * @param {HTMLInputElement} field The second identifier field.
 * @param {HTMLFormElement} form The create account form.
 * @param {Object} params The current identifer data.
 */
const checkSecondIdentifierAvailaibility = (field, form, params) => {
  cube.ajax
    .post(services.IDENTIFIER_EXISTS, params)
    .onSuccess(response => {
      checkSecondIdentifierAvailaibilitySuccess(response, form, params);
    })
    .onError(response => {
      ajaxError(response);
    })
    .call();
};

/**
 * Do some actions depending on whether the identifier exists or not.
 *
 * @param {Object} response The XHR response.
 * @param {HTMLFormElement} form The create account form.
 * @param {Object} params The current identifer data.
 */
const checkSecondIdentifierAvailaibilitySuccess = (response, form, params) => {
  const { data, errors } = response && response.responseTO;

  setStoredIdentifier(params, 'SECOND');

  // Mobile is already used on SEVERAL accounts
  if (!data && errors && errors.find(item => item.code === 'account.create.duplicateNumber')) {
    cube.alert.error(i18n('account.create.duplicateNumber'));
    disableSubmit(true);
  }

  // Mobile is already used on ONE account
  else if (data && data.identifierExists && data.identifierType === 'MOBILE') {
    dispatchActions(data.actions, form, 'SECOND');
    displayOptions(true, 'SECOND', 'it-is-my-mobile-option');
  }

  // Mobile is available
  else {
    if(data.identifierType === 'EMAIL') {
      displaySecondIdentifierMobileValidationButton(false);
    }else {
      displaySecondIdentifierMobileValidationButton();
    }
    disableSubmit(false);
  }
};

/**
 * Display or hide the validation code process for mobile identifier.
 *
 * @param {Boolean} displayed Display or hide the validation process.
 * @param {Object} params The current identifer data.
 * @param {String} identiferType 'MAIN' or 'SECOND' identifier.
 */
export const displayMobileValidation = (displayed, params, identifierType = 'MAIN') => {
  const phoneValidation =
    identifierType === 'MAIN' ? cube.$id('js-phone-validation') : cube.$id('js-second-identifier-phone-validation');

  if (!phoneValidation) return;

  if (displayed) {
    phoneValidation.classList.remove('hidden');
    phoneValidation.classList.add('account-form__field--disabled');
    phoneValidation.$('.js-resend-code-button').style.display = 'none';
    sendValidationCode(params, phoneValidation);
  } else {
    phoneValidation.classList.add('hidden');
    phoneValidation.classList.remove('account-form__field--disabled');
    counters.forEach(counter =>
      $(counter)
        .data('countdown')
        .stop()
    );
  }
};

/**
 * Display or hide the second identifier mobile verification button if it's activated.
 *
 * @param {Boolean} displayed Dispaly or hide the button.
 */
const displaySecondIdentifierMobileValidationButton = (displayed = true) => {
  const validation = cube.$id('js-mobile-validation');

  if (!validation) return;

  if (displayed) validation.classList.remove('hidden');
  else validation.classList.add('hidden');
};

/**
 * Display the mobile validation input for the second identifier
 *
 * @param {Event} event
 */
const sendSecondIdentifierValidationCode = event => {
  event.preventDefault();

  const data = getStoredIdentifier('SECOND');

  displaySecondIdentifierMobileValidationButton(false);
  displayMobileValidation(true, data, 'SECOND');
};

/**
 * Send an XHR to send an SMS with a validation code to the current mobile identifier.
 *
 * @param {Object} params The current identifer data.
 * @param {HTMLInputElement} input The phone validation input.
 */
const sendValidationCode = (params, input) => {
  cube.ajax
    .post(services.SEND_VALIDATION_CODE, params)
    .onSuccess(response => sendValidationCodeSuccess(response, input))
    .onError(ajaxError)
    .call();
};

/**
 * Display a message and launch a counter if the XHR returns no error(s).
 *
 * @param {Object} response The XHR response.
 */
const sendValidationCodeSuccess = (response, input) => {
  if (ajaxError(response)) return;

  disableSubmit(false);

  input.classList.remove('account-form__field--disabled');
  counters.forEach(counter =>
    $(counter)
      .data('countdown')
      .go()
  );
  cube.alert.information(i18n('profile.smsSent'));
};

/**
 * Resend a new validation Code
 *
 * @param {Event} event
 */
function resendValidationCode(event) {
  event.preventDefault();

  const identiferType = this.dataset.identifierType;
  const data = getStoredIdentifier(identiferType);

  displayMobileValidation(true, data, identiferType);
}

export const rules = {
  rules: {
    mobile: {
      mobile: true,
    },
  },
};



// WEBPACK FOOTER //
// ./src/customer/complete-account/complete-account-identifier.js